import { zodResolver } from '@hookform/resolvers/zod'
import { parseTime, Time } from '@internationalized/date'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { z } from 'zod'

import { ApiBusinessDayResponse } from '@/ducks/account/account.schema'
import { ApiGetBusinessHours } from '@/ducks/account/account.types'
import {
  useUpdateBranchBusinessHoursMutation,
  useUpdateBusinessHoursMutation
} from '@/ducks/account/api/account.api'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  selectSelectedBranchId,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { capitalize } from '@/utils/string.utils'
import { pushGtmFormSubmitEvent } from '@/utils/gtm.utils'
import {
  getLocalTime,
  getTimestampFromTime,
  sanitizeTimeZone,
  WEEKDAYS
} from '@/utils/time.utils'

const WeekdaySchema = z.object({
  day_name: z.enum(WEEKDAYS),
  start_time: z.string().nullable(),
  end_time: z.string().nullable(),
  isWorking: z.boolean(),
  slots: z.array(
    z.object({
      start_time: z.string().nullable(),
      end_time: z.string().nullable()
    })
  )
})
type WeekdayValue = z.infer<typeof WeekdaySchema>

const formSchema = z.object({
  businessHours: z.array(WeekdaySchema)
})
type FormData = z.infer<typeof formSchema>
type ErrorHours = z.infer<typeof ApiBusinessDayResponse>
const mapApiToForm = (data: ApiGetBusinessHours) => {
  const { timezone, businessHours } = data
  return {
    businessHours: businessHours.map(weekday => ({
      ...weekday,
      start_time: getLocalTime(weekday.start_time, sanitizeTimeZone(timezone)),
      end_time: getLocalTime(weekday.end_time, sanitizeTimeZone(timezone)),
      slots: weekday.slots.map(slot => ({
        start_time: getLocalTime(slot.start_time, sanitizeTimeZone(timezone)),
        end_time: getLocalTime(slot.end_time, sanitizeTimeZone(timezone))
      }))
    }))
  }
}

const mapFormToApi = (data: FormData, timezone: string) => {
  const { businessHours } = data
  const tz = sanitizeTimeZone(timezone)
  return {
    businessHours: businessHours.map(weekday => ({
      ...weekday,
      start_time: getTimestampFromTime(weekday.start_time, tz),
      end_time: getTimestampFromTime(weekday.end_time, tz),
      slots: weekday.slots.map(slot => ({
        start_time: getTimestampFromTime(slot.start_time, tz),
        end_time: getTimestampFromTime(slot.end_time, tz)
      }))
    }))
  }
}

const useBusinessHoursForm5Slots = (initialData: ApiGetBusinessHours) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const location = useLocation()
  const isBranchBusinessHours = location.pathname.includes(
    '/branch/business-hours'
  )
  const storeId = useSelector(selectStoreId) || ''
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorHours, setErrorHours] = useState<object | null>(null)
  const [slotsModified, setSlotsModified] = useState(false)
  const [updateBranchBusinessHours] = useUpdateBranchBusinessHoursMutation()
  const selectedBranchId = useSelector(selectSelectedBranchId)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: mapApiToForm(initialData)
  })

  const weekdays = form.watch(
    WEEKDAYS.map((_, index) => `businessHours.${index}` as const)
  )

  const handleSubmit = form.handleSubmit(async values => {
    pushGtmFormSubmitEvent()
    const activeId = isBranchBusinessHours ? selectedBranchId : storeId
    setErrorMessage(null)
    setErrorHours(null)
    const DTO = mapFormToApi(values, initialData.timezone)
    return updateBranchBusinessHours({ storeId: activeId, ...DTO })
      .unwrap()
      .then(({ message }) => {
        if (message === 'success') {
          dispatch(
            addSystemNotification({
              message: t('account.business-hours.success'),
              intent: 'success',
              duration: 3000
            })
          )
          form.reset(values)
          setSlotsModified(false)
        } else {
          throw Error(message)
        }
      })
      .catch(err => {
        // Format city hours in store/city timezone (backend validates in city TZ)
        if (err?.cityBusinessHours?.length) {
          const tz = sanitizeTimeZone(initialData.timezone)
          const formatInTz = (ms: number) =>
            new Date(ms).toLocaleTimeString([], {
              timeZone: tz,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })
          const transformedArray = err.cityBusinessHours.map(
            (item: ErrorHours) =>
              t('account.business-hours.invalid.description', {
                day_name: capitalize(item.day_name),
                start_time: formatInTz(item.start_time),
                end_time: formatInTz(item.end_time)
              })
          )
          setErrorMessage(t('account.business-hours.error'))
          setErrorHours(transformedArray)
        }
        dispatch(
          addSystemNotification({
            message: t('account.business-hours.error'),
            intent: 'error',
            duration: 3000
          })
        )
      })
  })

  const handleAddSlot = useCallback(
    (index: number) => {
      const currentSlots = form.getValues(`businessHours.${index}.slots`)
      const slotsCount = currentSlots.length

     
      if (slotsCount >= 4) return

      // Get the end time of the previous slot (or main slot if no additional slots)
      let previousEndTime: string | null = null
      if (slotsCount === 0) {
        previousEndTime = form.getValues(`businessHours.${index}.end_time`)
      } else {
        previousEndTime = currentSlots[slotsCount - 1].end_time
      }

      const endTime = previousEndTime ? parseTime(previousEndTime) : null

      const newSlot = {
        start_time: endTime
          ? endTime
              .add({ hours: 1 })
              .toString()
              .split(':')
              .slice(0, 2)
              .join(':')
          : null,
        end_time: '23:30'
      }

      form.setValue(
        `businessHours.${index}.slots`,
        [...currentSlots, newSlot],
        { shouldDirty: true }
      )
      setSlotsModified(true)
    },
    [form]
  )

  const handleDeleteSlot = useCallback(
    (index: number, slotIndex: number) => {
     
      if (slotIndex === -1) {
        form.setValue(`businessHours.${index}.start_time`, null, {
          shouldDirty: true
        })
        form.setValue(`businessHours.${index}.end_time`, null, {
          shouldDirty: true
        })
        setSlotsModified(true)
      } else {
        // Delete additional slot
        const currentSlots = form.getValues(`businessHours.${index}.slots`)
        const updatedSlots = currentSlots.filter((_, i) => i !== slotIndex)
        form.setValue(`businessHours.${index}.slots`, updatedSlots, {
          shouldDirty: true
        })
        setSlotsModified(true)
      }
    },
    [form]
  )

  const getSlotsCount = useCallback(
    (index: number) => {
      const slots = form.getValues(`businessHours.${index}.slots`)
      return slots.length
    },
    [form]
  )

  const getFormattedValue = useCallback(
    (weekdayValue: WeekdayValue) => {
      const firstSlotFormattedValue =
        weekdayValue.start_time && weekdayValue.end_time
          ? `${weekdayValue.start_time}-${weekdayValue.end_time}`
          : t('account.business-hours.not-set')

      const additionalSlotsFormatted = weekdayValue.slots
        .filter(slot => slot.start_time && slot.end_time)
        .map(slot => `${slot.start_time}-${slot.end_time}`)

      return [firstSlotFormattedValue, ...additionalSlotsFormatted].join(' / ')
    },
    [t]
  )

  const shouldDisableAdd = useCallback(
    (index: number) => {
      const currentSlots = form.getValues(`businessHours.${index}.slots`)
      const slotsCount = currentSlots.length

      if (slotsCount >= 4) return true

      // Only check if we have slots and the last one ends very close to midnight
      if (slotsCount > 0) {
        const lastSlot = currentSlots[slotsCount - 1]
        if (lastSlot.end_time) {
          const endTime = parseTime(lastSlot.end_time)
          // Only disable if the last slot ends at 23:58 or later
          return endTime.compare(new Time(23, 58)) >= 0
        }
      }

      return false
    },
    [form]
  )

  const getMixMaxRules = useCallback(
    (index: number, slotIndex: number | null, field: 'start' | 'end') => {
      const weekdayValue = form.getValues(`businessHours.${index}`)
      const { end_time, start_time } = weekdayValue
      const currentSlots = weekdayValue.slots

      // Main slot (first row)
      if (slotIndex === null) {
        if (field === 'start') {
          return {
            min: null,
            max: end_time
          }
        } else {
          // end field
          const hasSlots = currentSlots.length > 0
          return {
            min: start_time,
            max: hasSlots ? currentSlots[0]?.start_time || '23:01' : null
          }
        }
      }

      // Additional slots
      const slot = currentSlots[slotIndex]
      if (field === 'start') {
        // Get previous slot end time
        const previousEndTime =
          slotIndex === 0
            ? end_time
            : currentSlots[slotIndex - 1]?.end_time || null

        return {
          min: previousEndTime,
          max: slot?.end_time || '23:30'
        }
      } else {
        // end field
        const nextStartTime =
          slotIndex < currentSlots.length - 1
            ? currentSlots[slotIndex + 1]?.start_time || null
            : null

        return {
          min: slot?.start_time,
          max: nextStartTime
        }
      }
    },
    [form]
  )

  return {
    form,
    handleAddSlot,
    handleDeleteSlot,
    getSlotsCount,
    getFormattedValue,
    handleSubmit,
    shouldDisableAdd,
    getMixMaxRules,
    errorMessage,
    errorHours,
    slotsModified
  } as const
}

export { useBusinessHoursForm5Slots }

